import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { sendOtp, verifyOtp, normalizeIL } from "./edgeFns";

interface OtpDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (phone: string) => void;
  defaultPhone?: string;
}

export default function OtpDialog({
  open,
  onClose,
  onSuccess,
  defaultPhone = "",
}: OtpDialogProps) {
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [phone, setPhone] = useState(defaultPhone);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    let t: number | undefined;
    if (cooldown > 0) {
      t = window.setInterval(() => setCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    }
    return () => {
      if (t) clearInterval(t);
    };
  }, [cooldown]);

  useEffect(() => {
    if (!open) {
      setStep("phone");
      setPhone(defaultPhone || "");
      setCode("");
      setCooldown(0);
      setLoading(false);
    }
  }, [open, defaultPhone]);

  const disabledSend = useMemo(() => {
    const p = normalizeIL(phone);
    return !/^\+\d{7,15}$/.test(p) || cooldown > 0 || loading;
  }, [phone, cooldown, loading]);

  const disabledVerify = useMemo(() => code.trim().length < 4 || loading, [code, loading]);

  async function handleSend() {
    try {
      setLoading(true);
      const p = normalizeIL(phone);
      await sendOtp(p);
      toast.success("קוד נשלח בהצלחה");
      setStep("code");
      setCooldown(30);
    } catch (e: any) {

      const msg = e?.error || e?.message || "שליחת קוד נכשלה";
      toast.error(typeof msg === "string" ? msg : "שליחת קוד נכשלה");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify() {
    try {
      setLoading(true);
      const p = normalizeIL(phone);
      await verifyOtp(p, code.trim());
      toast.success("אומתת בהצלחה");
      onSuccess(p);
      onClose();
    } catch (e: any) {

      const key = e?.error;
      if (key === "code_invalid") toast.error("קוד לא נכון");
      else if (key === "code_expired") toast.error("הקוד פג תוקף");
      else if (key === "code_not_found") toast.error("לא נמצא קוד פעיל");
      else toast.error(e?.message || "שגיאה באימות הקוד");
    } finally {
      setLoading(false);
    }
  }

  function resend() {
    if (cooldown === 0 && !loading) handleSend();
  }

  if (!open) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-sm bg-white overflow-hidden"
          style={{ borderRadius: '1rem' }}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#E5E7EB' }}>
            <h3 className="text-lg font-semibold" style={{ color: '#1C1C1E' }}>
              {step === "phone" ? "מספר טלפון" : "קוד אימות"}
            </h3>
            <button onClick={onClose} className="p-2">
              <X size={20} style={{ color: '#6B7280' }} />
            </button>
          </div>

          {step === "phone" && (
            <div className="p-6 space-y-4">
              <input
                dir="ltr"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                className="w-full px-4 py-3 border outline-none"
                style={{
                  borderRadius: '0.75rem',
                  borderColor: '#E5E7EB',
                  fontSize: '16px',
                }}
                placeholder="05X-XXX-XXXX"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <button
                disabled={disabledSend}
                onClick={handleSend}
                className="w-full py-3 text-white font-semibold disabled:opacity-50"
                style={{
                  backgroundColor: '#3B82F6',
                  borderRadius: '0.75rem',
                }}
              >
                {loading ? "שולח..." : "שלח קוד"}
              </button>
              {cooldown > 0 && (
                <p className="text-center text-sm" style={{ color: '#6B7280' }}>
                  ניתן לשלוח שוב בעוד {cooldown} שניות
                </p>
              )}
            </div>
          )}

          {step === "code" && (
            <div className="p-6 space-y-4">
              <input
                inputMode="numeric"
                autoFocus
                className="w-full px-4 py-3 border outline-none text-center text-2xl font-bold tracking-widest"
                style={{
                  borderRadius: '0.75rem',
                  borderColor: '#E5E7EB',
                }}
                placeholder="● ● ● ● ● ●"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                maxLength={6}
              />
              <button
                disabled={disabledVerify}
                onClick={handleVerify}
                className="w-full py-3 text-white font-semibold disabled:opacity-50"
                style={{
                  backgroundColor: '#10B981',
                  borderRadius: '0.75rem',
                }}
              >
                {loading ? "מאמת..." : "אמת"}
              </button>
              <div className="flex items-center justify-between text-sm">
                <button
                  className="font-medium"
                  style={{ color: '#3B82F6' }}
                  onClick={() => { setStep("phone"); setCode(""); }}
                >
                  שנה מספר
                </button>
                <button
                  onClick={resend}
                  disabled={cooldown > 0 || loading}
                  className="font-medium disabled:opacity-50"
                  style={{ color: '#6B7280' }}
                >
                  שלח שוב {cooldown > 0 ? `(${cooldown})` : ""}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
